import { Card } from 'sections/Benefits/types';

export type AdvantagesCard = Omit<Card, 'link'>;

export type VisibilityCard = Omit<Card, 'icon' | 'link' | 'alt'> & {
  titleColor: 'future' | 'amber' | 'cucumber' | 'disco-man' | 'kill-bill';
};

export type SchemeItem = {
  id: number;
  name: string;
  mainIconPath: string;
  mainIconAlt: string;
  sideIconPath: string;
  sideIconAlt: string;
  sideLine: boolean;
  topLine?: boolean;
  bottomLine?: boolean;
}

export type SchemeItems = {
  list01: SchemeItem[];
  list02: SchemeItem[];
  list03: SchemeItem[];
  list04: SchemeItem[];
}

export const advantagesCards: AdvantagesCard[] = [
  {
    id: 1,
    icon: 'icon-route.svg',
    title: 'End-to-end tracking',
    description: 'Track your multimodal shipments from origin to destination.',
    alt: 'End-to-end tracking',
  },
  {
    id: 2,
    icon: 'icon-link.svg',
    title: 'Supply chain visibility',
    description: 'Understand the availability of your products for every purchase order.',
    alt: 'Supply chain visibility',
  },
  {
    id: 3,
    icon: 'icon-location.svg',
    title: 'Manage by exception',
    description: 'Receive email notifications for key milestone events and intransit exceptions.',
    alt: 'Manage by exception',
  },
  {
    id: 4,
    icon: 'icon-chart.svg',
    title: 'Insight reports',
    description: 'Gain insight on your logistics network performance and optimization opportunities.',
    alt: 'Insight reports',
  },
  {
    id: 5,
    icon: 'icon-watch.svg',
    title: 'Realtime visibility',
    description: 'Track multimodal shipments and products from origin to destination.',
    alt: 'Realtime visibility',
  },
];

export const visibilityCards: VisibilityCard[] = [
  {
    id: 1,
    title: '79%+',
    titleColor: 'future',
    description: 'Companies have inadequate visibility into their supply chain',
  },
  {
    id: 2,
    title: '90%+',
    titleColor: 'amber',
    description: 'Shippers use visibility capabilities as a metric when choosing carriers',
  },
  {
    id: 3,
    title: '63%+',
    titleColor: 'cucumber',
    description: 'Shippers prioritise improving supply chain visibility',
  },
  {
    id: 4,
    title: '6%+',
    titleColor: 'disco-man',
    description: 'Shippers have real-time or near real-time freight visibility',
  },
  {
    id: 5,
    title: '50%+',
    titleColor: 'cucumber',
    description: 'Shipping cost savings from improved supply chain visibility',
  },
  {
    id: 6,
    title: '15%+',
    titleColor: 'disco-man',
    description: 'Reduction in time manually searching for a cargo',
  },
  {
    id: 7,
    title: '75%+',
    titleColor: 'kill-bill',
    description: 'Customer growth due to satisfaction in freight visibility',
  },
  {
    id: 8,
    title: '20%+',
    titleColor: 'amber',
    description: 'Global shipping lines’ vessel arrival delay',
  },
];

export const schemeItems: SchemeItems = {
  list01: [
    {
      id: 1,
      name: 'Booking confirmation',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ticket.svg',
      mainIconAlt: 'Ticket Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-mark.svg',
      sideIconAlt: 'Pause Icon',
      sideLine: true,
    },
    {
      id: 2,
      name: 'Gate appointment for container',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-conveyor.svg',
      mainIconAlt: 'Conveyor Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-mark.svg',
      sideIconAlt: 'Pause Icon',
      sideLine: true,
    },
    {
      id: 3,
      name: 'Truck gate in for container pickup',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ramp.svg',
      mainIconAlt: 'Ramp Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-mark.svg',
      sideIconAlt: 'Pause Icon',
      sideLine: true,
    },
    {
      id: 4,
      name: 'Empty container gate out',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-truck.svg',
      mainIconAlt: 'Truck icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-mark.svg',
      sideIconAlt: 'Pause Icon',
      sideLine: true,
    },
    {
      id: 5,
      name: 'Truck arrival at customer facility',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-militia.svg',
      mainIconAlt: 'Militia Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 6,
      name: 'Container stuffed',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-container.svg',
      mainIconAlt: 'Container icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-location.svg',
      sideIconAlt: 'Location pin icon',
      sideLine: true,
    },
    {
      id: 7,
      name: 'Gate appointment for container drop-off',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-pipe.svg',
      mainIconAlt: 'Pipe icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-location.svg',
      sideIconAlt: 'Location pin icon',
      sideLine: true,
    },
    {
      id: 8,
      name: 'Full container gate in',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ramp.svg',
      mainIconAlt: 'Ramp Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 9,
      name: 'Container in yard',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-truck.svg',
      mainIconAlt: 'Truck icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 10,
      name: 'Truck gate out',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-militia.svg',
      mainIconAlt: 'Militia Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
      bottomLine: true,
    },
  ],
  list02: [
    {
      id: 1,
      name: 'Vessel berth booking',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ticket.svg',
      mainIconAlt: 'Ticket Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
      topLine: true,
    },
    {
      id: 2,
      name: 'Truck gate out',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-conveyor.svg',
      mainIconAlt: 'Conveyor Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 3,
      name: 'Container loaded on vessel',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ramp.svg',
      mainIconAlt: 'Ramp Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 4,
      name: 'Unloaded from rail',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-truck.svg',
      mainIconAlt: 'Truck icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 5,
      name: 'Rail arrival',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-militia.svg',
      mainIconAlt: 'Militia Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 6,
      name: 'BOL issuance',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ticket.svg',
      mainIconAlt: 'Ticket Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 7,
      name: 'Rail departure',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-conveyor.svg',
      mainIconAlt: 'Conveyor Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 8,
      name: 'Loaded on rail',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ramp.svg',
      mainIconAlt: 'Ramp Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-location.svg',
      sideIconAlt: 'Location pin icon',
      sideLine: true,
    },
    {
      id: 9,
      name: 'Customs release',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-truck.svg',
      mainIconAlt: 'Truck icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 10,
      name: 'Export customs declarations',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-militia.svg',
      mainIconAlt: 'Militia Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-location.svg',
      sideIconAlt: 'Location pin icon',
      sideLine: true,
    },
  ],
  list03: [
    {
      id: 1,
      name: 'Vessel berthing',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ticket.svg',
      mainIconAlt: 'Ticket Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 2,
      name: 'Container discharged from vessel',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-conveyor.svg',
      mainIconAlt: 'Conveyor Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 3,
      name: 'Container in yard',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ramp.svg',
      mainIconAlt: 'Ramp Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 4,
      name: 'Import customs clearance',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-truck.svg',
      mainIconAlt: 'Truck icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 5,
      name: 'Customs release',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-militia.svg',
      mainIconAlt: 'Militia Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 6,
      name: 'Delivery order issuance',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ticket.svg',
      mainIconAlt: 'Ticket Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 7,
      name: 'Terminal release',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-conveyor.svg',
      mainIconAlt: 'Conveyor Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 8,
      name: 'Gate appointment for container pickup',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ramp.svg',
      mainIconAlt: 'Ramp Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 9,
      name: 'Truck gate in for container pickup',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-truck.svg',
      mainIconAlt: 'Truck icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 10,
      name: 'Mount on truck',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-militia.svg',
      mainIconAlt: 'Militia Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
      bottomLine: true,
    },
  ],
  list04: [
    {
      id: 1,
      name: 'Milestone',
      mainIconPath: '',
      mainIconAlt: '',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: false,
    },
    {
      id: 2,
      name: 'GPS based tracking',
      mainIconPath: '',
      mainIconAlt: '',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-location.svg',
      sideIconAlt: 'Location pin icon',
      sideLine: false,
    },
    {
      id: 3,
      name: 'Empty container returned',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ticket.svg',
      mainIconAlt: 'Ticket Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 4,
      name: 'Cargo acceptance',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-conveyor.svg',
      mainIconAlt: 'Conveyor Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 5,
      name: 'Container destuffed',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-ramp.svg',
      mainIconAlt: 'Ramp Icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 6,
      name: 'Truck arrival at customer facility',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-truck.svg',
      mainIconAlt: 'Truck icon',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
    {
      id: 7,
      name: 'Truck gate out',
      mainIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-militia.svg',
      mainIconAlt: 'Militia Icon for Cargo Flow',
      sideIconPath: '/images/pages/DigitalTools/CargoesFlow/scheme-icon-timer.svg',
      sideIconAlt: 'Timeline Icon',
      sideLine: true,
    },
  ],
};
